<template>
  <q-dialog ref="dialogRef" persistent @hide="onDialogHide">
    <q-card
      :style="{
        minWidth: '600px',
      }"
      class="relative-position elevation-1"
    >
      <q-card-section
        class="flex justify-between items-center q-px-2x-md q-py-xl"
      >
        <span class="text-lg text-neutral text-weight-medium">
          Send for signature
        </span>

        <PBtn flat dense round size="sm" @click="() => onDialogCancel()">
          <CloseIcon class="icon" />
        </PBtn>
      </q-card-section>

      <q-separator></q-separator>

      <q-card-section
        class="rounded-borders text-neutral-90 q-px-2x-md q-py-xl"
      >
        <q-form @submit="onSubmit">
          <div class="row">
            <div class="col-3">
              <div class="text-sm text-neutral q-mt-xs">
                Contract Name
                <span class="text-negative">*</span>
              </div>
            </div>
            <div class="col-9">
              <PInput
                v-model="contractName"
                :validations="{
                  required: true,
                }"
                placeholder="Enter contract name"
              />
            </div>
          </div>

          <PTable
            ref="pTable"
            v-model:selected="selected"
            :columns="columns"
            :rows="signatories"
            :row-key="'key'"
            class="text-xs q-mt-lg q-mb-sm"
            table-header-class="text-neutral text-weight-medium bg-primary-20"
            flat
            bordered
            hide-bottom
            wrap-cells
            selection="multiple"
          >
            <template
              v-for="(slot, index) of Object.keys($slots)"
              :key="index"
              #[slot]="slotProps"
            >
              <slot v-bind="slotProps" :name="slot"></slot>
            </template>
            <template #header-cell="props">
              <q-th :props="props">
                {{ props.col.label }}
                <span v-if="props.col.required" class="text-red">*</span>
              </q-th>
            </template>
            <template #body-cell-name="props">
              <q-td :props="props">
                <PInput
                  v-model="props.row[props.col.name]"
                  class="text-capitalize"
                  :disable="
                    Boolean(props.row._id) || Boolean(props.row.isDisabled)
                  "
                  :required="selected.includes(props.row)"
                  hide-bottom-space
                />
              </q-td>
            </template>
            <template #body-cell-email="props">
              <q-td :props="props">
                <PInput
                  v-model.trim="props.row[props.col.name]"
                  :required="selected.includes(props.row)"
                  :validations="{
                    type: 'email',
                  }"
                  hide-bottom-space
                  :disable="Boolean(props.row.isDisabled)"
                />
              </q-td>
            </template>
            <template #body-cell-phone="props">
              <q-td :props="props">
                <PInput
                  v-model="props.row[props.col.name]"
                  maxlength="10"
                  :validations="{
                    type: 'phoneNumber',
                    length: 10,
                  }"
                  hide-bottom-space
                  :disable="Boolean(props.row.isDisabled)"
                />
              </q-td>
            </template>
          </PTable>
          <PBtn flat class="q-pa-none" @click="addNewSignatory">
            <AddIcon class="icon--sm q-mr-xs" />
            <span class="text-sm text-weight-medium">Add new signatory</span>
          </PBtn>
          <div class="flex justify-end q-mt-xl">
            <PBtn
              :fluid="false"
              :loading="isLoading"
              label="Send"
              type="submit"
              :disable="!selected.length || selected.length > 3"
            >
              <PTooltip v-if="!selected.length"
                >Please select atleast 1 signatory</PTooltip
              >
              <PTooltip v-if="selected.length > 3"
                >Maximum signatories allowed are 3</PTooltip
              >
            </PBtn>
          </div>
        </q-form>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { useDialogPluginComponent, useQuasar } from "quasar";

import CloseIcon from "assets/icons/close.svg";
import AddIcon from "assets/icons/add.svg";
import { unref } from "vue";
import { faker } from "@faker-js/faker";
import PConfirmDialog from "src/components/PConfirmDialog.vue";

const $q = useQuasar();

defineEmits([...useDialogPluginComponent.emits]);

const { user } = storeToRefs(useUserStore());

const { putUserDocumentSignatureStatus } = useUserStore();

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } =
  useDialogPluginComponent();

const isLoading = ref(false);

const propsData = defineProps({
  fileName: {
    type: String,
    required: true,
  },
  documentId: {
    type: String,
    required: true,
  },
});

const columns = computed(() => {
  return [
    {
      align: "left",
      name: "name",
      label: "Signatory",
      field: "name",
      required: true,
    },
    {
      align: "left",
      name: "email",
      label: "Email",
      field: "email",
      required: true,
    },
    {
      align: "left",
      name: "phone",
      label: "Contact No.",
      field: "phone",
      required: false,
    },
  ];
});

const selected = ref([]);

const computedDirectorList = computed(
  () => unref(user)?.businessDetails?.directors,
);

const signatories = ref([]);

const initializeSignatories = () => {
  signatories.value = unref(computedDirectorList).map((director) => ({
    key: faker.random.alphaNumeric(16),
    _id: director._id,
    name: director.name,
    phone: director.phone || "",
    email:
      director.email ||
      (director.is_primary ? unref(user)?.basicInfo?.email : ""),
  }));

  let accountOwner = unref(user).account_owner;

  if (accountOwner) {
    signatories.value.push({
      key: faker.random.alphaNumeric(16),
      _id: accountOwner._id,
      name: accountOwner.name,
      phone: accountOwner.phone || "",
      email: accountOwner.email || "",
      isDisabled: true,
    });
  }
};

const addNewSignatory = () => {
  signatories.value.push({
    key: faker.random.alphaNumeric(16),
    name: "",
    phone: "",
    email: "",
  });
};

const contractName = ref(
  `${propsData.fileName} - ${unref(user)?.businessDetails?.shop_name}`,
);

const onSubmit = async () => {
  let signatoriesNamesSelected = selected.value
    .map((s) => `${s.name} (${s.email})`)
    .join(", ");
  let confirmationMessage = `Are you sure you want to send ${contractName.value} to ${signatoriesNamesSelected} ?`;

  try {
    $q.dialog({
      component: PConfirmDialog,
      componentProps: {
        text: `${confirmationMessage}`,
        isFooterVisible: true,
        submittable: true,
        cancellable: true,
        cancelLabel: "Cancel",
        submitLabel: "Send",
        closable: false,
        onConfirm: async () => {
          try {
            isLoading.value = true;

            await putUserDocumentSignatureStatus({
              document_id: propsData.documentId,
              status_cd: "awaiting_response",
              signatories: unref(selected),
              contract_name: unref(contractName),
            });

            isLoading.value = false;

            onDialogOK();
          } catch (error) {
            isLoading.value = false;

            console.error(error);
          }
        },
      },
    });
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  initializeSignatories();
});
</script>
